import { useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  ArrowDownToLine,
  ArrowUpFromLine,
  ChevronDown,
  ContactRound,
  LayoutGrid,
  Loader2,
  Pin,
  Plus,
  Search,
} from "lucide-react";
import type { ContactListItem } from "@multi-chat/shared";
import { CONTACT_LIST_PAGE_SIZES, type ContactListSortName } from "@multi-chat/shared";
import { ActiveFilterBar, activeFilterChip } from "../../../components/common/ActiveFilterBar/ActiveFilterBar.js";
import { WorkspaceDrawerTrigger } from "../../../components/layout/WorkspaceDrawerTrigger.js";
import { Button } from "../../../components/ui/button/index.js";
import { AlertDialog, AlertDialogDescription, AlertDialogTitle } from "../../../components/ui/alert-dialog/index.js";
import {
  contactsModalFooterCompactClass,
  contactsModalHeaderCompactClass,
} from "../contacts-dialog-presentation.js";
import { ContactsModalContent } from "../ContactsModalContent.js";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "../../../components/ui/dropdown-menu/index.js";
import { ErrorState } from "../../../components/common/ErrorState/index.js";
import { ProductState } from "../../../components/common/ProductState/index.js";
import { ListSkeleton } from "../../../components/common/WorkspaceLoader.js";
import { LtrValue } from "../../../components/common/LtrValue.js";
import { cn } from "../../../lib/utils.js";
import { reportColumnHeaderDragHandlers } from "../../reports/report-column-header-drag.js";
import type { ContactTableColumnId, ContactTableUiDir } from "../contacts-column-view.js";
import {
  CONTACT_TABLE_ACTIONS_WIDTH,
  CONTACT_TABLE_CHECKBOX_WIDTH,
  CONTACT_TABLE_DEFAULT_WIDTHS,
  contactPinnedStickyOffset,
  contactStickyEndStyle,
  contactStickyStartStyle,
} from "../contacts-column-view.js";
import { useContactTableColumns } from "../use-contact-table-columns.js";
import { ContactsColumnHeaderMenu } from "../ContactsColumnHeaderMenu.js";
import { ContactsColumnsPopover } from "../ContactsColumnsPopover.js";
import { ContactsFilterPopover } from "../ContactsFilterPopover.js";
import {
  CONTACT_TABLE_COLUMN_META,
  contactsTableBodyCellClass,
  contactsTableBodyRowClass,
  contactsTableClass,
  contactsTableHeadRowClass,
  contactsTableHeaderCellClass,
  contactsTableHeaderIconWrapClass,
  contactsTableHeaderLabelClass,
  contactsTableHeaderStickyTopClass,
  contactsTableHoverViewClass,
  contactsTablePinnedEdgeClass,
  contactsTableScrollViewportClass,
  contactsTableShellClass,
  contactsTableStickyBodyCellClass,
  contactsTableStickyHeaderCellClass,
  contactsValueChipClass,
  contactsViewportDropdownClass,
} from "../contacts-table-presentation.js";
import { useContacts } from "../use-contacts.js";
import {
  activeContactsFilterCount,
  EMPTY_CONTACTS_FILTERS,
  hasActiveContactsFilters,
} from "../contacts-list-query.js";
import {
  canOfferSelectAllActive,
  canOfferSelectMatching,
  contactsHeaderCheckboxState,
  contactsSelectedCount,
  isContactRowSelected,
  syncContactsCheckboxIndeterminate,
} from "../contacts-selection.js";
import {
  createContactGroup,
  deleteContactGroup,
  exportContactsCsv,
  renameContactGroup,
} from "../../../api/contacts-api.js";
import { ContactDetailSheet } from "./ContactDetailSheet.js";
import { ContactFormSheet } from "./ContactFormSheet.js";
import { ContactGroupPreview } from "../ContactGroupPreview.js";
import { ContactRowActionsMenu } from "./ContactRowActionsMenu.js";
import { ContactsBulkActionsBar } from "./ContactsBulkActionsBar.js";
import { ContactsMobileGroupsSheet } from "./ContactsMobileGroupsSheet.js";
import { ImportContactsDialog } from "./ImportContactsDialog.js";
import { LinkedPlatformBadges } from "../LinkedPlatformBadges.js";
import { formatContactBirthday } from "../ContactBirthdayField.js";
import { useNotifications } from "../../notifications/notification-context.js";

function formatContactDate(iso: string, locale: string): string {
  return new Intl.DateTimeFormat(locale, {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  }).format(new Date(iso));
}

function ContactTableHeaderCell({
  columnId,
  label,
  pinned,
  stickyOffset,
  isLastPinned,
  tableDir,
  dragFromRef,
  onReorderColumn,
  onTogglePin,
  onHide,
}: {
  columnId: ContactTableColumnId;
  label: string;
  pinned: boolean;
  stickyOffset: number | null;
  isLastPinned: boolean;
  tableDir: ContactTableUiDir;
  dragFromRef: React.MutableRefObject<ContactTableColumnId | null>;
  onReorderColumn: (fromId: ContactTableColumnId, toId: ContactTableColumnId) => void;
  onTogglePin: () => void;
  onHide: () => void;
}): React.JSX.Element {
  const meta = CONTACT_TABLE_COLUMN_META[columnId];
  const Icon = meta?.icon;
  const width = CONTACT_TABLE_DEFAULT_WIDTHS[columnId];
  const isSticky = stickyOffset != null;

  return (
    <th
      className={cn(
        contactsTableHeaderCellClass,
        "relative text-start",
        meta?.align === "end" && "text-end",
        isSticky ? contactsTableStickyHeaderCellClass : contactsTableHeaderStickyTopClass,
        isLastPinned && contactsTablePinnedEdgeClass,
      )}
      style={
        isSticky
          ? {
              ...contactStickyStartStyle(stickyOffset, tableDir),
              top: 0,
              width,
              minWidth: width,
              maxWidth: width,
            }
          : { width, minWidth: width, top: 0 }
      }
      data-testid={`contact-column-${columnId}`}
      data-pinned={pinned ? "true" : undefined}
      {...reportColumnHeaderDragHandlers(columnId, dragFromRef, onReorderColumn)}
    >
      <span
        className={cn(
          contactsTableHeaderLabelClass,
          "w-full",
          meta?.align === "end" && "justify-end",
        )}
      >
        {Icon ? (
          <span className={contactsTableHeaderIconWrapClass} aria-hidden="true">
            <Icon size={12} />
          </span>
        ) : null}
        <span className="min-w-0 truncate">{label}</span>
        {pinned ? (
          <Pin size={11} className="shrink-0 text-ink-muted" aria-hidden="true" />
        ) : null}
        <ContactsColumnHeaderMenu
          columnId={columnId}
          pinned={pinned}
          onTogglePin={onTogglePin}
          onHide={onHide}
        />
      </span>
    </th>
  );
}

export function ContactsPage({ organizationId }: { organizationId: number }): React.JSX.Element {
  const { t, i18n } = useTranslation();
  const { pushNotification } = useNotifications();
  const tableDir: ContactTableUiDir = i18n.dir() === "rtl" ? "rtl" : "ltr";
  const contacts = useContacts(organizationId);
  const dragFromRef = useRef<ContactTableColumnId | null>(null);
  const {
    columnOrder,
    visibleColumns,
    hiddenColumns,
    pinnedColumns,
    pinnedVisible,
    toggleColumn,
    togglePin,
    moveColumn,
    reorderColumn,
    resetColumns,
  } = useContactTableColumns();
  const [createOpen, setCreateOpen] = useState(false);
  const [importOpen, setImportOpen] = useState(false);
  const [importJobId, setImportJobId] = useState<string | null>(null);
  const [bulkBusy, setBulkBusy] = useState(false);
  const [exportBusy, setExportBusy] = useState(false);
  const [mobileGroupsOpen, setMobileGroupsOpen] = useState(false);
  const [deleteAllInGroup, setDeleteAllInGroup] = useState<{
    id: number;
    name: string;
    memberCount: number;
  } | null>(null);

  useEffect(() => {
    const onOpenImport = (event: Event): void => {
      const jobId = (event as CustomEvent<{ jobId?: string }>).detail?.jobId ?? null;
      setImportJobId(jobId);
      setImportOpen(true);
    };
    window.addEventListener("open-contact-import", onOpenImport);
    const onUpdated = (): void => {
      void contacts.reloadList();
      void contacts.reloadGroups();
    };
    window.addEventListener("contact-import-updated", onUpdated);
    return () => {
      window.removeEventListener("open-contact-import", onOpenImport);
      window.removeEventListener("contact-import-updated", onUpdated);
    };
  }, []);

  const filterCount = activeContactsFilterCount(contacts.filters);
  const hasFilters = hasActiveContactsFilters(contacts.filters);
  const hasSearch = contacts.searchInput.trim().length > 0;
  const visibleIds = contacts.list.map((row) => Number(row.id));
  const selectedCount = contactsSelectedCount(
    contacts.selection,
    contacts.totalCount,
    contacts.orgActiveCount,
    contacts.matchingQueryKey,
  );
  const headerSelection = contactsHeaderCheckboxState(
    contacts.selection,
    visibleIds,
    contacts.matchingQueryKey,
  );
  const showSelectMatching = canOfferSelectMatching(
    contacts.selection,
    contacts.list.length,
    contacts.totalCount,
    visibleIds,
  );
  const showSelectAllActive = canOfferSelectAllActive(
    contacts.selection,
    contacts.totalCount,
    contacts.orgActiveCount,
    selectedCount,
  );

  const locale = i18n.language.startsWith("he") ? "he-IL" : "en-US";
  const rangeStart =
    contacts.totalCount === 0 ? 0 : ((contacts.query.page ?? 1) - 1) * (contacts.query.pageSize ?? 25) + 1;
  const rangeEnd = Math.min(
    (contacts.query.page ?? 1) * (contacts.query.pageSize ?? 25),
    contacts.totalCount,
  );

  const sortOptions: Array<{ value: ContactListSortName; label: string }> = useMemo(
    () => [
      { value: "FIRST_NAME_ASC", label: t("contacts.sort.firstNameAsc") },
      { value: "FIRST_NAME_DESC", label: t("contacts.sort.firstNameDesc") },
      { value: "LAST_NAME_ASC", label: t("contacts.sort.lastNameAsc") },
      { value: "LAST_NAME_DESC", label: t("contacts.sort.lastNameDesc") },
      { value: "CREATED_AT_DESC", label: t("contacts.sort.createdNewest") },
      { value: "CREATED_AT_ASC", label: t("contacts.sort.createdOldest") },
      { value: "EMAIL_ASC", label: t("contacts.sort.emailAsc") },
      { value: "EMAIL_DESC", label: t("contacts.sort.emailDesc") },
    ],
    [t],
  );

  const handleExport = async (contactIds?: number[]) => {
    const ids = contactIds ?? (contacts.selectedIds.size > 0 ? [...contacts.selectedIds] : undefined);
    const blob = await exportContactsCsv(organizationId, {
      contactIds: ids,
      query: ids ? undefined : contacts.effectiveQuery,
    });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = "contacts.csv";
    a.click();
    URL.revokeObjectURL(url);
  };

  const runExport = async (contactIds?: number[]) => {
    if (exportBusy) return;
    setExportBusy(true);
    try {
      await handleExport(contactIds);
    } finally {
      setExportBusy(false);
    }
  };

  const clearFiltersAndSearch = () => {
    contacts.setFilters(EMPTY_CONTACTS_FILTERS);
    contacts.setSearchInput("");
  };
  const clearAllContactFilters = () => {
    clearFiltersAndSearch();
    contacts.setSelectedGroupId(null);
    contacts.setPage(1);
  };
  const selectedGroup = contacts.groups.find((group) => group.id === contacts.selectedGroupId);
  const contactActiveFilters = [
    activeFilterChip("search", t("contacts.filters.search"), contacts.searchInput, () => {
      contacts.setSearchInput("");
    }),
    activeFilterChip("group", t("contacts.filters.group"), selectedGroup?.name, () => {
      contacts.setSelectedGroupId(null);
      contacts.setPage(1);
    }),
    activeFilterChip(
      "email",
      t("contacts.filters.email"),
      contacts.filters.hasEmail && contacts.filters.hasEmail !== "ANY"
        ? t(`contacts.filters.hasEmail.${contacts.filters.hasEmail.toLowerCase()}`)
        : null,
      () => contacts.setFilters({ ...contacts.filters, hasEmail: undefined }),
    ),
    activeFilterChip(
      "phone",
      t("contacts.filters.phone"),
      contacts.filters.hasPhone && contacts.filters.hasPhone !== "ANY"
        ? t(`contacts.filters.hasPhone.${contacts.filters.hasPhone.toLowerCase()}`)
        : null,
      () => contacts.setFilters({ ...contacts.filters, hasPhone: undefined }),
    ),
    activeFilterChip(
      "platform",
      t("contacts.filters.platform"),
      contacts.filters.platform && contacts.filters.platform !== "ANY"
        ? t(`contacts.filters.platformOptions.${contacts.filters.platform.toLowerCase()}`)
        : null,
      () => contacts.setFilters({ ...contacts.filters, platform: undefined }),
    ),
    activeFilterChip("createdFrom", t("contacts.filters.createdFrom"), contacts.filters.createdFrom, () =>
      contacts.setFilters({ ...contacts.filters, createdFrom: undefined }),
    ),
    activeFilterChip("createdTo", t("contacts.filters.createdTo"), contacts.filters.createdTo, () =>
      contacts.setFilters({ ...contacts.filters, createdTo: undefined }),
    ),
  ].flatMap((chip) => (chip ? [chip] : []));

  const handleCreateGroup = async (name: string) => {
    const trimmed = name.trim();
    if (!trimmed) return;
    await createContactGroup(organizationId, trimmed);
    await contacts.reloadGroups();
  };

  const handleRenameGroup = async (groupId: number, name: string) => {
    await renameContactGroup(organizationId, groupId, name);
    await contacts.reloadGroups();
  };

  const handleDeleteGroup = async (groupId: number) => {
    await deleteContactGroup(organizationId, groupId);
    if (contacts.selectedGroupId === groupId) {
      contacts.setSelectedGroupId(null);
    }
    await contacts.reloadGroups();
    await contacts.reloadList();
  };

  const handleCopyId = async (contactId: number) => {
    try {
      await navigator.clipboard.writeText(String(contactId));
      pushNotification({ message: t("contacts.row.idCopied") });
    } catch {
      /* clipboard may be denied */
    }
  };

  const showEmpty =
    !contacts.loading && !contacts.error && contacts.totalCount === 0 && !contacts.searchInput.trim();

  return (
    <div className="flex h-full min-h-0 flex-col bg-canvas">
      <div className="flex min-h-0 flex-1">
        <div className="flex min-h-0 min-w-0 flex-1 flex-col">
          <header className="flex flex-wrap items-center justify-between gap-3 border-b border-line/70 bg-[linear-gradient(180deg,rgba(255,255,255,0.98)_0%,rgba(248,250,252,0.94)_100%)] px-4 py-3.5">
            <div className="flex items-center gap-2.5">
              <span className="inline-grid size-9 place-items-center rounded-xl border border-line/70 bg-surface text-accent shadow-[inset_0_1px_0_rgba(255,255,255,0.75)]">
                <LayoutGrid size={18} aria-hidden="true" />
              </span>
              <div>
                <h1 className="text-[1rem] font-semibold tracking-[-0.01em] text-foreground">
                  {t("contacts.title")}
                </h1>
                <p className="text-[0.75rem] text-muted">{t("contacts.pageSubtitle")}</p>
              </div>
            </div>
            <div className="flex flex-wrap items-center gap-2">
              <WorkspaceDrawerTrigger
                label={t("contacts.mobile.groupsButton")}
                open={mobileGroupsOpen}
                onClick={() => setMobileGroupsOpen(true)}
                testId="contacts-groups-trigger"
                ariaLabel={t("contacts.mobile.groupsButton")}
              />
              <Button
                variant="outline"
                size="sm"
                disabled={exportBusy}
                onClick={() => void runExport()}
              >
                {exportBusy ? (
                  <Loader2 size={16} className="animate-spin" aria-hidden="true" />
                ) : (
                  <ArrowUpFromLine size={16} aria-hidden="true" />
                )}
                {exportBusy ? t("contacts.actions.exporting") : t("contacts.actions.export")}
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={() => {
                  setImportJobId(null);
                  setImportOpen(true);
                }}
              >
                <ArrowDownToLine size={16} aria-hidden="true" />
                {t("contacts.actions.import")}
              </Button>
              <Button size="sm" onClick={() => setCreateOpen(true)}>
                <Plus size={16} aria-hidden="true" />
                {t("contacts.actions.create")}
              </Button>
            </div>
          </header>

          <div className="flex flex-wrap items-center gap-2 border-b border-line/50 bg-surface/80 px-4 py-2.5">
            <div className="relative min-w-[12rem] flex-1 max-w-md">
              <Search
                size={16}
                className="pointer-events-none absolute start-2.5 top-1/2 -translate-y-1/2 text-muted"
                aria-hidden="true"
              />
              <input
                type="search"
                value={contacts.searchInput}
                onChange={(e) => contacts.setSearchInput(e.target.value)}
                placeholder={t("contacts.searchPlaceholder")}
                className="w-full rounded-lg border border-line/70 bg-background py-2 ps-9 pe-3 text-[0.8125rem] shadow-sm transition-[border-color,box-shadow] focus-visible:outline-2 focus-visible:outline-offset-1 focus-visible:outline-[color-mix(in_srgb,var(--color-accent)_55%,transparent)]"
              />
            </div>
            <ContactsFilterPopover
              filters={contacts.filters}
              activeCount={filterCount}
              onChange={contacts.setFilters}
              onClearAll={() => contacts.setFilters(EMPTY_CONTACTS_FILTERS)}
            />
            <ContactsColumnsPopover
              columnOrder={columnOrder}
              hiddenColumns={hiddenColumns}
              onToggle={toggleColumn}
              onMove={moveColumn}
              onReorder={reorderColumn}
              onReset={resetColumns}
            />
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="outline" size="sm" className="h-8 gap-1.5 text-[0.75rem]">
                  {t("contacts.actions.sort")}
                  <ChevronDown size={14} aria-hidden="true" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent
                align="end"
                collisionPadding={12}
                className={cn("min-w-48", contactsViewportDropdownClass)}
              >
                {sortOptions.map((opt) => (
                  <DropdownMenuItem key={opt.value} onSelect={() => contacts.setSort(opt.value)}>
                    {contacts.query.sort === opt.value ? "✓ " : ""}
                    {opt.label}
                  </DropdownMenuItem>
                ))}
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
          <ActiveFilterBar
            chips={contactActiveFilters}
            onClearAll={clearAllContactFilters}
            clearTestId="contacts-clear-all-filters"
          />

          <ContactsBulkActionsBar
            selectedCount={selectedCount}
            selectionMode={contacts.selection.mode}
            matchingTotal={contacts.totalCount}
            orgActiveCount={contacts.orgActiveCount}
            excludedCount={
              contacts.selection.mode === "ids" ? 0 : contacts.selection.excludedIds.size
            }
            showSelectMatching={showSelectMatching}
            showSelectAllActive={showSelectAllActive}
            allowExport={contacts.selection.mode === "ids"}
            groups={contacts.groups}
            busy={bulkBusy}
            exportBusy={exportBusy}
            onClearSelection={contacts.clearSelection}
            onSelectMatching={contacts.selectMatchingQuery}
            onSelectAllActive={contacts.selectAllActive}
            onExportSelected={async () => {
              await runExport([...contacts.selectedIds]);
            }}
            onDeleteSelected={async () => {
              setBulkBusy(true);
              try {
                const result = await contacts.bulkDeleteSelected();
                pushNotification({
                  message: t("contacts.bulk.deletedNotice", { count: result.deleted }),
                });
              } finally {
                setBulkBusy(false);
              }
            }}
            onBulkGroups={async (mode, selection) => {
              setBulkBusy(true);
              try {
                const result = await contacts.bulkUpdateGroups(mode, selection);
                const notice =
                  mode === "add"
                    ? t("contacts.bulk.addedToGroupsNotice", {
                        count: result.updated,
                        groupCount: selection.groupIds.length + selection.newGroupNames.length,
                      })
                    : mode === "remove"
                      ? t("contacts.bulk.removedFromGroupsNotice", { count: result.updated })
                      : mode === "replace"
                        ? t("contacts.bulk.replacedGroupsNotice", { count: result.updated })
                        : t("contacts.bulk.clearedGroupsNotice", { count: result.updated });
                pushNotification({ message: notice });
              } finally {
                setBulkBusy(false);
              }
            }}
          />

          <div className="flex min-h-0 min-w-0 flex-1 flex-col overflow-hidden">
            {contacts.error ? (
              <ErrorState
                layout="panel"
                message={t("contacts.loadError")}
                onRetry={() => void contacts.reloadList()}
                retryLabel={t("contacts.retry")}
              />
            ) : contacts.loading ? (
              <ListSkeleton rows={8} label={t("contacts.loading")} testId="contacts-loading" />
            ) : showEmpty ? (
              <div
                className="flex flex-col items-center justify-center gap-3 p-12 text-center"
                data-testid="contacts-empty-state"
              >
                <span className="inline-grid size-12 place-items-center rounded-2xl border border-line/70 bg-surface text-ink-muted shadow-[inset_0_1px_0_rgba(255,255,255,0.7)]">
                  <ContactRound size={22} aria-hidden="true" />
                </span>
                <div className="grid max-w-sm gap-1">
                  <p className="m-0 text-[0.9375rem] font-semibold text-foreground">
                    {t("contacts.empty.title")}
                  </p>
                  <p className="m-0 text-[0.8125rem] text-muted">{t("contacts.empty.description")}</p>
                </div>
              </div>
            ) : contacts.list.length === 0 ? (
              <ProductState
                tone="filtered"
                testId="contacts-no-results"
                title={t("contacts.noResultsTitle")}
                description={t("contacts.noResults")}
              />
            ) : (
              <div className="flex min-h-0 min-w-0 flex-1 flex-col p-4">
                <div className={cn(contactsTableShellClass, contactsTableScrollViewportClass)}>
                  <table className={contactsTableClass}>
                    <colgroup>
                      <col style={{ width: CONTACT_TABLE_CHECKBOX_WIDTH }} />
                      {visibleColumns.map((columnId) => (
                        <col
                          key={columnId}
                          style={{ width: CONTACT_TABLE_DEFAULT_WIDTHS[columnId] }}
                        />
                      ))}
                      <col style={{ width: CONTACT_TABLE_ACTIONS_WIDTH }} />
                    </colgroup>
                    <thead>
                      <tr className={contactsTableHeadRowClass}>
                        <th
                          className={cn(
                            contactsTableHeaderCellClass,
                            contactsTableStickyHeaderCellClass,
                            "w-10",
                            pinnedVisible.length === 0 && contactsTablePinnedEdgeClass,
                          )}
                          style={{
                            ...contactStickyStartStyle(0, tableDir),
                            top: 0,
                            width: CONTACT_TABLE_CHECKBOX_WIDTH,
                            minWidth: CONTACT_TABLE_CHECKBOX_WIDTH,
                          }}
                        >
                          <input
                            type="checkbox"
                            aria-label={t("contacts.selectPage")}
                            data-testid="contacts-select-page"
                            ref={(node) => syncContactsCheckboxIndeterminate(node, headerSelection)}
                            checked={headerSelection === "all"}
                            onChange={() => contacts.toggleHeaderPageSelection()}
                          />
                        </th>
                        {visibleColumns.map((columnId) => {
                          const pinned = pinnedColumns.has(columnId);
                          const stickyOffset = pinned
                            ? contactPinnedStickyOffset(columnId, pinnedVisible)
                            : null;
                          const isLastPinned =
                            pinned &&
                            pinnedVisible.length > 0 &&
                            pinnedVisible[pinnedVisible.length - 1] === columnId;
                          return (
                            <ContactTableHeaderCell
                              key={columnId}
                              columnId={columnId}
                              label={t(`contacts.columns.${columnId}`)}
                              pinned={pinned}
                              stickyOffset={stickyOffset}
                              isLastPinned={Boolean(isLastPinned)}
                              tableDir={tableDir}
                              dragFromRef={dragFromRef}
                              onReorderColumn={reorderColumn}
                              onTogglePin={() => togglePin(columnId)}
                              onHide={() => toggleColumn(columnId)}
                            />
                          );
                        })}
                        <th
                          className={cn(
                            contactsTableHeaderCellClass,
                            contactsTableStickyHeaderCellClass,
                            "w-11 border-e-0 px-0.5",
                          )}
                          style={{
                            ...contactStickyEndStyle(0, tableDir),
                            top: 0,
                            width: CONTACT_TABLE_ACTIONS_WIDTH,
                            minWidth: CONTACT_TABLE_ACTIONS_WIDTH,
                          }}
                        >
                          <span className="sr-only">{t("contacts.row.actionsColumn")}</span>
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      {contacts.list.map((row) => (
                        <ContactRow
                          key={row.id}
                          row={row}
                          locale={locale}
                          tableDir={tableDir}
                          visibleColumns={visibleColumns}
                          pinnedVisible={pinnedVisible}
                          pinnedColumns={pinnedColumns}
                          selected={isContactRowSelected(
                            contacts.selection,
                            Number(row.id),
                            contacts.matchingQueryKey,
                          )}
                          busy={bulkBusy}
                          onToggleSelect={() => contacts.toggleSelect(row.id)}
                          onOpen={() => void contacts.openDetail(row.id)}
                          onCopyId={() => handleCopyId(row.id)}
                          onDelete={async () => {
                            await contacts.removeContact(row.id);
                            pushNotification({ message: t("contacts.notices.deleted") });
                          }}
                        />
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}
          </div>

          <footer className="flex flex-wrap items-center justify-between gap-3 border-t border-border/60 bg-surface px-4 py-2.5 text-[0.8125rem]">
            <span className="text-muted">
              {t("contacts.pagination.viewing", {
                start: rangeStart,
                end: rangeEnd,
                total: contacts.totalCount,
              })}
            </span>
            <div className="flex items-center gap-2">
              <select
                className="rounded-md border border-border bg-background px-2 py-1"
                value={contacts.query.pageSize ?? 25}
                onChange={(e) => contacts.setPageSize(Number(e.target.value))}
                aria-label={t("contacts.pagination.pageSize")}
              >
                {CONTACT_LIST_PAGE_SIZES.map((size) => (
                  <option key={size} value={size}>
                    {size}
                  </option>
                ))}
              </select>
              <Button
                variant="outline"
                size="sm"
                disabled={(contacts.query.page ?? 1) <= 1}
                onClick={() => contacts.setPage((contacts.query.page ?? 1) - 1)}
              >
                {t("contacts.pagination.previous")}
              </Button>
              <Button
                variant="outline"
                size="sm"
                disabled={(contacts.query.page ?? 1) >= contacts.totalPages}
                onClick={() => contacts.setPage((contacts.query.page ?? 1) + 1)}
              >
                {t("contacts.pagination.next")}
              </Button>
            </div>
          </footer>
        </div>
      </div>

      <ContactDetailSheet
        open={contacts.detailId != null}
        detail={contacts.detail}
        loading={contacts.detailLoading}
        groups={contacts.groups}
        organizationId={organizationId}
        onClose={contacts.closeDetail}
        onSave={async (body) => {
          if (contacts.detailId == null) return;
          await contacts.saveContact(contacts.detailId, body);
          pushNotification({ message: t("contacts.notices.updated") });
        }}
        onDelete={async () => {
          if (contacts.detailId == null) return;
          await contacts.removeContact(contacts.detailId);
          pushNotification({ message: t("contacts.notices.deleted") });
        }}
        onRestore={async () => {
          if (contacts.detailId == null) return;
          await contacts.restoreContactById(contacts.detailId);
          pushNotification({ message: t("contacts.notices.restored") });
        }}
        onMerged={async () => {
          await contacts.reloadList();
          if (contacts.detailId != null) {
            await contacts.openDetail(contacts.detailId);
          }
        }}
      />

      <ContactFormSheet
        open={createOpen}
        groups={contacts.groups}
        onClose={() => setCreateOpen(false)}
        onSubmit={async (body) => {
          await contacts.saveContact(null, body);
          pushNotification({ message: t("contacts.notices.created") });
          setCreateOpen(false);
        }}
      />

      <ImportContactsDialog
        open={importOpen}
        organizationId={organizationId}
        groups={contacts.groups}
        restoreJobId={importJobId}
        onClose={() => {
          setImportOpen(false);
          setImportJobId(null);
        }}
        onClearRestoreJobId={() => setImportJobId(null)}
        onJobStarted={() => {
          void contacts.reloadList();
          void contacts.reloadGroups();
        }}
        onGroupsChanged={() => contacts.reloadGroups()}
      />

      <ContactsMobileGroupsSheet
        open={mobileGroupsOpen}
        groups={contacts.groups}
        selectedGroupId={contacts.selectedGroupId}
        onClose={() => setMobileGroupsOpen(false)}
        onSelectAllContacts={() => {
          contacts.setSelectedGroupId(null);
          contacts.setPage(1);
        }}
        onSelectGroup={(groupId) => {
          contacts.setSelectedGroupId(groupId);
          contacts.setPage(1);
        }}
        onCreateGroup={handleCreateGroup}
        onRenameGroup={handleRenameGroup}
        onDeleteGroup={handleDeleteGroup}
        onDeleteAllContacts={setDeleteAllInGroup}
        allContactsCount={contacts.orgActiveCount}
      />

      <AlertDialog
        open={deleteAllInGroup != null}
        onOpenChange={(next) => {
          if (!next) setDeleteAllInGroup(null);
        }}
      >
        <ContactsModalContent data-testid="contacts-delete-all-in-group-dialog">
          <div className={contactsModalHeaderCompactClass}>
            <AlertDialogTitle className="text-[0.9375rem] font-semibold tracking-tight">
              {deleteAllInGroup
                ? t("contacts.bulk.deleteAllInGroupTitle", {
                    count: deleteAllInGroup.memberCount,
                    name: deleteAllInGroup.name,
                  })
                : null}
            </AlertDialogTitle>
            <AlertDialogDescription className="text-[0.8125rem] leading-snug">
              {t("contacts.bulk.deleteAllInGroupBody")}
            </AlertDialogDescription>
          </div>
          <div className={contactsModalFooterCompactClass}>
            <Button type="button" variant="ghost" size="sm" onClick={() => setDeleteAllInGroup(null)}>
              {t("common.cancel")}
            </Button>
            <Button
              type="button"
              size="sm"
              variant="destructive"
              disabled={bulkBusy || (deleteAllInGroup?.memberCount ?? 0) <= 0}
              data-testid="contacts-delete-all-in-group-confirm"
              onClick={() => {
                if (!deleteAllInGroup) return;
                const groupId = deleteAllInGroup.id;
                setBulkBusy(true);
                void contacts
                  .bulkDeleteAllInGroup(groupId)
                  .then((result) => {
                    pushNotification({
                      message: t("contacts.bulk.deleteAllInGroupNotice", { count: result.deleted }),
                    });
                  })
                  .finally(() => {
                    setBulkBusy(false);
                    setDeleteAllInGroup(null);
                  });
              }}
            >
              {t("contacts.bulk.deleteAllInGroup")}
            </Button>
          </div>
        </ContactsModalContent>
      </AlertDialog>
    </div>
  );
}

function ContactRow({
  row,
  locale,
  tableDir,
  visibleColumns,
  pinnedVisible,
  pinnedColumns,
  selected,
  busy,
  onToggleSelect,
  onOpen,
  onCopyId,
  onDelete,
}: {
  row: ContactListItem;
  locale: string;
  tableDir: ContactTableUiDir;
  visibleColumns: ContactTableColumnId[];
  pinnedVisible: ContactTableColumnId[];
  pinnedColumns: Set<ContactTableColumnId>;
  selected: boolean;
  busy: boolean;
  onToggleSelect: () => void;
  onOpen: () => void;
  onCopyId: () => void | Promise<void>;
  onDelete: () => Promise<void>;
}): React.JSX.Element {
  const { t } = useTranslation();

  const cellContent: Record<ContactTableColumnId, React.ReactNode> = {
    firstName: (
      <button
        type="button"
        className="min-w-0 truncate font-semibold text-foreground hover:text-accent hover:underline"
        onClick={onOpen}
      >
        {row.firstName ?? "—"}
      </button>
    ),
    lastName: <span className="text-foreground/90">{row.lastName ?? "—"}</span>,
    displayName: <span className="text-foreground/90">{row.displayName}</span>,
    email: row.email ? (
      <LtrValue className={cn(contactsValueChipClass, "max-w-full")} title={row.email}>
        {row.email}
      </LtrValue>
    ) : (
      <span className="text-muted">—</span>
    ),
    phone: row.normalizedPhoneNumber ? (
      <LtrValue className={cn(contactsValueChipClass, "max-w-full")} title={row.normalizedPhoneNumber}>
        {row.normalizedPhoneNumber}
      </LtrValue>
    ) : (
      <span className="text-muted">—</span>
    ),
    birthday: row.birthday ? (
      <span className="text-[0.8125rem] tabular-nums text-foreground/90" data-testid={`contact-row-birthday-${row.id}`}>
        {formatContactBirthday(row.birthday, locale)}
      </span>
    ) : (
      <span className="text-muted">—</span>
    ),
    groups: (
      <ContactGroupPreview
        groups={row.groups}
        empty={<span className="text-muted">—</span>}
        testId={`contact-row-groups-${row.id}`}
      />
    ),
    platforms: <LinkedPlatformBadges types={row.linkedPlatformTypes} />,
    createdAt: (
      <LtrValue className="text-[0.75rem] tabular-nums text-muted">
        {formatContactDate(row.createdAt, locale)}
      </LtrValue>
    ),
    updatedAt: (
      <LtrValue className="text-[0.75rem] tabular-nums text-muted">
        {formatContactDate(row.updatedAt, locale)}
      </LtrValue>
    ),
  };

  return (
    <tr className={contactsTableBodyRowClass} data-selected={selected || undefined}>
      <td
        className={cn(
          contactsTableBodyCellClass,
          contactsTableStickyBodyCellClass,
          "w-10",
          pinnedVisible.length === 0 && contactsTablePinnedEdgeClass,
        )}
        style={{
          ...contactStickyStartStyle(0, tableDir),
          width: CONTACT_TABLE_CHECKBOX_WIDTH,
          minWidth: CONTACT_TABLE_CHECKBOX_WIDTH,
        }}
      >
        <input type="checkbox" checked={selected} onChange={onToggleSelect} aria-label={row.displayName} />
      </td>
      {visibleColumns.map((columnId) => {
        const pinned = pinnedColumns.has(columnId);
        const stickyOffset = pinned
          ? contactPinnedStickyOffset(columnId, pinnedVisible)
          : null;
        const isLastPinned =
          pinned &&
          pinnedVisible.length > 0 &&
          pinnedVisible[pinnedVisible.length - 1] === columnId;
        const width = CONTACT_TABLE_DEFAULT_WIDTHS[columnId];
        return (
          <td
            key={columnId}
            className={cn(
              contactsTableBodyCellClass,
              "max-w-[14rem] min-w-0 overflow-hidden",
              CONTACT_TABLE_COLUMN_META[columnId]?.align === "end" && "text-end",
              stickyOffset != null && contactsTableStickyBodyCellClass,
              isLastPinned && contactsTablePinnedEdgeClass,
            )}
            style={
              stickyOffset != null
                ? {
                    ...contactStickyStartStyle(stickyOffset, tableDir),
                    width,
                    minWidth: width,
                    maxWidth: width,
                  }
                : { width, minWidth: width }
            }
            data-pinned={pinned ? "true" : undefined}
          >
            {cellContent[columnId]}
          </td>
        );
      })}
      <td
        className={cn(
          contactsTableBodyCellClass,
          contactsTableStickyBodyCellClass,
          "relative border-e-0 px-0.5",
        )}
        style={{
          ...contactStickyEndStyle(0, tableDir),
          width: CONTACT_TABLE_ACTIONS_WIDTH,
          minWidth: CONTACT_TABLE_ACTIONS_WIDTH,
        }}
      >
        <div className="flex items-center justify-center">
          <button
            type="button"
            className={contactsTableHoverViewClass}
            onClick={onOpen}
            data-testid={`contact-row-hover-view-${row.id}`}
          >
            {t("contacts.row.view")}
          </button>
          <ContactRowActionsMenu
            contactId={row.id}
            displayName={row.displayName}
            busy={busy}
            onView={onOpen}
            onCopyId={onCopyId}
            onDelete={onDelete}
          />
        </div>
      </td>
    </tr>
  );
}
